import type {
  CloudBoardState,
  CloudSnapshotHistory,
} from "@board-preserve/contracts";

export const CLOUD_HISTORY_REFRESH_INTERVAL_MS = 15_000;

export function applyHistoryScheduleState(
  current: CloudBoardState | null,
  history: CloudSnapshotHistory,
): CloudBoardState | null {
  if (!current || !history.scheduleState) return current;
  return {
    ...current,
    settings: {
      ...current.settings,
      nextSnapshotAt: history.scheduleState.nextSnapshotAt,
      scheduleStatus: history.scheduleState.scheduleStatus,
    },
  };
}

interface CloudHistoryRefreshOptions {
  clearIntervalImplementation?: typeof window.clearInterval;
  documentTarget?: Pick<
    Document,
    "addEventListener" | "removeEventListener" | "visibilityState"
  >;
  intervalMs?: number;
  refresh: () => void;
  setIntervalImplementation?: typeof window.setInterval;
  windowTarget?: Pick<Window, "addEventListener" | "removeEventListener">;
}

/**
 * Keeps retained-backup history current without polling a hidden Trello modal.
 * The immediate history read remains the caller's responsibility.
 */
export function startCloudHistoryRefresh({
  clearIntervalImplementation = window.clearInterval.bind(window),
  documentTarget = document,
  intervalMs = CLOUD_HISTORY_REFRESH_INTERVAL_MS,
  refresh,
  setIntervalImplementation = window.setInterval.bind(window),
  windowTarget = window,
}: CloudHistoryRefreshOptions): () => void {
  const refreshWhenVisible = (): void => {
    if (documentTarget.visibilityState === "visible") {
      refresh();
    }
  };
  const interval = setIntervalImplementation(refreshWhenVisible, intervalMs);
  documentTarget.addEventListener("visibilitychange", refreshWhenVisible);
  windowTarget.addEventListener("focus", refreshWhenVisible);

  return () => {
    clearIntervalImplementation(interval);
    documentTarget.removeEventListener("visibilitychange", refreshWhenVisible);
    windowTarget.removeEventListener("focus", refreshWhenVisible);
  };
}
